// Central JSON-LD (Schema.org) builders for the site.
// All builders return plain JSON objects ready to drop into <SEO structuredData={[...]} />.
//
// Why centralise? So the home page, service pages, and Krina's
// Person entity all share the same canonical @id values and stay
// consistent for entity graphing across Google + LLM citation engines.

import { PUBLIC_SITE_URL, KRINA_PORTRAIT_IMAGE, DEFAULT_OG_IMAGE, BRAND_LOGO_IMAGE } from './seo';
import { contactData } from './mock';
import { getPracticeSnapshot, toPostalAddressLD, toGeoLD, toOpeningHoursLD } from './practice';

// Stable @id anchors — keep these URLs stable forever even if you change copy.
// Google and AI engines deduplicate entity references using @id.
export const CLINIC_ID = `${PUBLIC_SITE_URL}/#clinic`;
export const KRINA_ID = `${PUBLIC_SITE_URL}/#krina`;
export const KRINA_PHYSICIAN_ID = `${PUBLIC_SITE_URL}/#krina-physician-practice`;
export const ORG_ID = `${PUBLIC_SITE_URL}/#organization`;
export const WEBSITE_ID = `${PUBLIC_SITE_URL}/#website`;
export const PLACE_ID = `${PUBLIC_SITE_URL}/#place`;

// E.164 phone for schema.org (the display format lives in contactData.phone).
export const PHONE_E164 = `+${contactData.whatsapp}`;

// Aggregate rating — update these as Google reviews grow. The hero stat tile
// still shows the LIVE value via the /api/google-rating endpoint; this constant
// only seeds the JSON-LD that crawlers see in the source HTML.
export const CLINIC_AGGREGATE_RATING = {
  ratingValue: 4.9,
  reviewCount: 39,
  bestRating: 5,
  worstRating: 1,
};

// When were the service pages last clinically reviewed?
// Bump this whenever Krina updates a treatment-approach modality.
export const LAST_REVIEWED = "2026-02-01";

// Krina's public social profiles (for sameAs entity graphing).
// Extend this list as new profiles go live (LinkedIn, Podcast pages, YouTube etc.)
export const KRINA_SAME_AS = [
  "https://www.instagram.com/krinapanchal/",
  "https://maps.google.com/?cid=12085728095023003487", // Google Business Profile
];

// Clinic-level social + directory profiles (superset of KRINA_SAME_AS).
// Google uses these to disambiguate The TMD Physio from other physio clinics
// and to build the entity graph that powers the Knowledge Panel.
export const CLINIC_SAME_AS = [
  "https://www.instagram.com/krinapanchal/",
  "https://maps.google.com/?cid=12085728095023003487",
  "https://www.krinapanchalphysio.com/",
];

// Krina Panchal — Person entity for E-E-A-T signals.
export const getPersonKrinaLD = () => ({
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": KRINA_ID,
  name: "Krina Panchal",
  givenName: "Krina",
  familyName: "Panchal",
  jobTitle: "HCPC-registered Specialist TMD Physiotherapist",
  description:
    "Krina Panchal is an HCPC-registered specialist physiotherapist with dual BSc (Hons) degrees from Manchester Metropolitan University (Biomedical Science + Physiotherapy), a Masters Module from the University of Liverpool in Assessment and Management of TMD, and 15+ years of clinical experience focused on temporomandibular disorders (TMD), cervicogenic headaches, migraines, somatosensory tinnitus and craniofacial pain. Her postgraduate training spans the Steve Kraus Physical Therapy TMD course (Atlanta, 2014), an Orofacial Pain Clinic placement at Columbia University (2014), CRAFTA® Foundation Parts 1 & 2 (Italy, 2016), the Watson Headache® Foundation Course (2017), Instituto Rocabado's BOSCO TMD Course (2017), the Institute for Functional Medicine AFMCP Foundation Course (Cohort 108, Washington DC), dry needling, and nutritional therapy. She has been invited to lecture on UCL's MSc Audiology programme (2025) and is a past Committee Member of the Association of Chartered Physiotherapists treating Temporomandibular Disorders (ACPTMD, 2017–2019).",
  image: KRINA_PORTRAIT_IMAGE,
  url: PUBLIC_SITE_URL,
  worksFor: { "@id": CLINIC_ID },
  alumniOf: [
    { "@type": "CollegeOrUniversity", name: "Manchester Metropolitan University", url: "https://www.mmu.ac.uk" },
    { "@type": "CollegeOrUniversity", name: "University of Liverpool", url: "https://www.liverpool.ac.uk" },
    { "@type": "EducationalOrganization", name: "The Institute for Functional Medicine (IFM)", url: "https://www.ifm.org" },
    { "@type": "EducationalOrganization", name: "Instituto Rocabado" },
  ],
  affiliation: [
    { "@type": "CollegeOrUniversity", name: "University College London (UCL)", url: "https://www.ucl.ac.uk" },
    { "@type": "Organization", name: "Chartered Society of Physiotherapy" },
    { "@type": "Organization", name: "Health and Care Professions Council" },
  ],
  knowsAbout: [
    "Temporomandibular Joint Disorder (TMD)",
    "Chronic Headache",
    "Migraine",
    "Somatic Tinnitus",
    "Facial Pain",
    "Trigeminal Neuralgia",
    "Bruxism",
    "Watson Headache® Approach",
    "Craniofacial Therapy",
    "Dry Needling",
    "Intra-oral Massage",
    "Myofascial Taping",
    "Pilates",
    "Functional Medicine",
    "Nutritional Therapy",
    "Biomedical Science",
    "Trauma-informed care",
    "Multidisciplinary rehabilitation",
    "Speech and Language Therapy (Assistant Practitioner)",
    "Psychotherapy",
  ],
  hasCredential: [
    { "@type": "EducationalOccupationalCredential", credentialCategory: "Degree", name: "BSc (Hons) Biomedical Science", recognizedBy: { "@type": "CollegeOrUniversity", name: "Manchester Metropolitan University" } },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "Degree", name: "BSc (Hons) Physiotherapy", recognizedBy: { "@type": "CollegeOrUniversity", name: "Manchester Metropolitan University" } },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "Postgraduate Module", name: "Masters Module — Assessment and Management of TMD", recognizedBy: { "@type": "CollegeOrUniversity", name: "University of Liverpool", url: "https://www.liverpool.ac.uk" } },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "Certification", name: "Steve Kraus Physical Therapy & TMD Course (Atlanta, USA)", validFrom: "2014", dateCreated: "2014" },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "Clinical Placement", name: "Orofacial Pain Clinic — Shadowing", validFrom: "2014", dateCreated: "2014", recognizedBy: { "@type": "CollegeOrUniversity", name: "Columbia University", url: "https://www.columbia.edu" } },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "Certification", name: "CRAFTA® Foundation Course, Parts 1 & 2 (Italy)", validFrom: "2016", dateCreated: "2016", recognizedBy: { "@type": "Organization", name: "CRAFTA® Cranio Facial Therapy Academy" } },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "Certification", name: "Watson Headache® Foundation Course (London)", validFrom: "2017", dateCreated: "2017", recognizedBy: { "@type": "Organization", name: "Watson Headache® Institute" } },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "Certification", name: "Instituto Rocabado — BOSCO TMD Course", validFrom: "2017", dateCreated: "2017", recognizedBy: { "@type": "Organization", name: "Instituto Rocabado" } },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "Certification", name: "Institute of Functional Medicine — AFMCP Foundation Course (Cohort 108), Washington DC, USA", recognizedBy: { "@type": "Organization", name: "The Institute for Functional Medicine" } },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "Diploma", name: "Nutritional Therapy Diploma (London)" },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "Certification", name: "Dry Needling, Levels 1 & 2 (London)" },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "Certification", name: "Myofascial Taping Foundation (London)" },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "Certification", name: "Pilates (London)" },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "Diploma", name: "Diploma in Massage (London)" },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "Teaching Role", name: "Invited Lecturer — MSc Audiology", validFrom: "2025", recognizedBy: { "@type": "CollegeOrUniversity", name: "University College London (UCL)", url: "https://www.ucl.ac.uk" } },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "Membership", name: "Chartered Society of Physiotherapy (CSP)" },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "Membership", name: "Health and Care Professions Council (HCPC)" },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "Role", name: "Past Committee Member — Association of Chartered Physiotherapists treating Temporomandibular Disorders (ACPTMD), 2017–2019", validFrom: "2017", validThrough: "2019" },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "Clinical Role", name: "NHS Speech and Language Therapy Assistant Practitioner" },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "Postgraduate Certificate", name: "PG Cert in Psychotherapy" },
  ],
  memberOf: [
    { "@type": "Organization", name: "Chartered Society of Physiotherapy" },
    { "@type": "Organization", name: "Health and Care Professions Council" },
    { "@type": "Organization", name: "Association of Chartered Physiotherapists treating Temporomandibular Disorders (ACPTMD)" },
  ],
  sameAs: KRINA_SAME_AS,
});

// Organization entity (publisher of the site).
export const getOrganizationLD = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": ORG_ID,
  name: "The TMD Physio",
  url: PUBLIC_SITE_URL,
  logo: {
    "@type": "ImageObject",
    url: BRAND_LOGO_IMAGE,
    width: 868,
    height: 937,
  },
  founder: { "@id": KRINA_ID },
  sameAs: CLINIC_SAME_AS,
});

// WebSite entity — enables the Google "Sitelinks search box" rich result.
// The `potentialAction.SearchAction` lets Google render a search box directly
// inside the SERP result, letting users search krinapanchalphysio.com from
// Google without visiting first. Query string is captured as `q`.
export const getWebSiteLD = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  url: PUBLIC_SITE_URL,
  name: "The TMD Physio",
  publisher: { "@id": ORG_ID },
  inLanguage: "en-GB",
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${PUBLIC_SITE_URL}/tmd-knowledge-base?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
});

// -----------------------------------------------------------------------------
// Physician — schema.org's MedicalOrganization sub-type for an individual
// practitioner's clinical practice. This is distinct from the Person entity
// (which represents Krina the human) and gives AI search engines a THIRD
// explicit signal alongside MedicalClinic + Person that this site is a
// legitimate physician entity for medical queries.
//
// Emitted globally from App.js alongside MedicalClinic + Person.
// -----------------------------------------------------------------------------
export const getPhysicianLD = (practice = getPracticeSnapshot()) => ({
  "@context": "https://schema.org",
  "@type": "Physician",
  "@id": KRINA_PHYSICIAN_ID,
  name: "Krina Panchal — Specialist TMD & Craniofacial Physiotherapy Practice",
  alternateName: [
    "Krina Panchal Physiotherapy",
    "The TMD Physio Practice",
  ],
  description:
    "Krina Panchal's specialist physiotherapy practice at The TMD Physio Clinic, focused on evidence-based, drug-free rehabilitation for temporomandibular disorders (TMD), craniofacial pain, chronic headache, migraine, somatic tinnitus and trigeminal neuralgia. The practice combines clinical physiotherapy training with additional expertise from her NHS Speech and Language Therapy Assistant Practitioner role and PG Cert in Psychotherapy, delivering a multidisciplinary, trauma-informed approach to craniomandibular and craniocervical disorders.",
  url: PUBLIC_SITE_URL,
  image: KRINA_PORTRAIT_IMAGE,
  telephone: PHONE_E164,
  email: contactData.email,
  address: toPostalAddressLD(practice),
  areaServed: [
    { "@type": "City", name: "London" },
    { "@type": "AdministrativeArea", name: "Waterloo" },
    { "@type": "Country", name: "United Kingdom" },
  ],
  medicalSpecialty: [
    "Physiotherapy",
    "Musculoskeletal",
    "Neurology",
  ],
  availableService: [
    { "@type": "MedicalTherapy", name: "Temporomandibular Joint Disorder (TMD) Treatment", url: `${PUBLIC_SITE_URL}/services/tmd-treatment` },
    { "@type": "MedicalTherapy", name: "Headache & Migraine Physiotherapy", url: `${PUBLIC_SITE_URL}/services/headache-migraine-relief` },
    { "@type": "MedicalTherapy", name: "Somatic Tinnitus Management", url: `${PUBLIC_SITE_URL}/services/tinnitus-management` },
    { "@type": "MedicalTherapy", name: "Facial Pain & Trigeminal Neuralgia Rehabilitation", url: `${PUBLIC_SITE_URL}/services/facial-pain-treatment` },
  ],
  practitioner: { "@id": KRINA_ID },
  founder: { "@id": KRINA_ID },
  parentOrganization: { "@id": CLINIC_ID },
  memberOf: [
    { "@type": "Organization", name: "Chartered Society of Physiotherapy" },
    { "@type": "Organization", name: "Health and Care Professions Council" },
  ],
  sameAs: KRINA_SAME_AS,
});


// MedicalClinic — the headline local entity for "TMD physio London"
// queries and for Google Knowledge Panel / AI Overviews.
export const getMedicalClinicLD = (practice = getPracticeSnapshot()) => ({
  "@context": "https://schema.org",
  "@type": ["MedicalClinic", "Physiotherapy", "LocalBusiness"],
  "@id": CLINIC_ID,
  name: "The TMD Physio",
  alternateName: ["The TMD Physio @ Waterloo", "Krina Panchal Physiotherapy"],
  description:
    "Specialist head, neck and temporomandibular disorder (TMD) physiotherapy clinic for jaw pain, cervicogenic headaches, migraines, somatosensory tinnitus and craniofacial pain. Founded by Krina Panchal, based in Waterloo, London.",
  url: PUBLIC_SITE_URL,
  logo: BRAND_LOGO_IMAGE,
  image: KRINA_PORTRAIT_IMAGE,
  telephone: PHONE_E164,
  email: contactData.email,
  priceRange: "£60–£550",
  paymentAccepted: ["Cash", "Credit Card", "Debit Card", "Bank Transfer", "Health Insurance"],
  currenciesAccepted: "GBP",
  address: toPostalAddressLD(practice),
  geo: toGeoLD(practice),
  hasMap: "https://maps.google.com/?cid=12085728095023003487",
  openingHoursSpecification: toOpeningHoursLD(practice),
  areaServed: [
    { "@type": "City", name: "London" },
    { "@type": "AdministrativeArea", name: "Waterloo" },
    { "@type": "AdministrativeArea", name: "South Bank" },
    { "@type": "AdministrativeArea", name: "Lambeth" },
    { "@type": "AdministrativeArea", name: "Southwark" },
    { "@type": "AdministrativeArea", name: "Westminster" },
    { "@type": "Country", name: "United Kingdom" },
  ],
  medicalSpecialty: ["Physiotherapy", "Musculoskeletal", "Neurology"],
  availableService: [
    { "@type": "MedicalTherapy", name: "TMD Treatment", url: `${PUBLIC_SITE_URL}/services/tmd-treatment` },
    { "@type": "MedicalTherapy", name: "Headache & Migraine Relief", url: `${PUBLIC_SITE_URL}/services/headache-migraine-relief` },
    { "@type": "MedicalTherapy", name: "Tinnitus Management", url: `${PUBLIC_SITE_URL}/services/tinnitus-management` },
    { "@type": "MedicalTherapy", name: "Facial Pain Treatment", url: `${PUBLIC_SITE_URL}/services/facial-pain-treatment` },
    { "@type": "MedicalTherapy", name: "Massage", url: `${PUBLIC_SITE_URL}/services/massage` },
    { "@type": "Service", name: "The Signature Jaw Massage", url: `${PUBLIC_SITE_URL}/wellness-massage` },
    { "@type": "MedicalTherapy", name: "NESA neuromodulation", url: `${PUBLIC_SITE_URL}/technology-and-treatments#nesa` },
    { "@type": "MedicalTherapy", name: "Low-level laser therapy", url: `${PUBLIC_SITE_URL}/technology-and-treatments#laser` },
    { "@type": "MedicalTherapy", name: "Dry needling", url: `${PUBLIC_SITE_URL}/technology-and-treatments#dry-needling` },
    { "@type": "MedicalTherapy", name: "TMJ Velvet radiofrequency & diathermy", url: `${PUBLIC_SITE_URL}/technology-and-treatments#radiofrequency` },
  ],
  knowsAbout: [
    "Physiotherapy",
    "Temporomandibular Joint Disorders",
    "Craniofacial Pain",
    "Temporomandibular joint dysfunction",
    "Bruxism",
    "Somatosensory tinnitus",
    "Migraine",
    "Cervicogenic headache",
    "Facial pain",
    "Trigeminal neuralgia",
    "NESA neuromodulation",
  ],
  founder: { "@id": KRINA_ID },
  employee: { "@id": KRINA_ID },
  sameAs: CLINIC_SAME_AS,
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: CLINIC_AGGREGATE_RATING.ratingValue,
    reviewCount: CLINIC_AGGREGATE_RATING.reviewCount,
    bestRating: CLINIC_AGGREGATE_RATING.bestRating,
    worstRating: CLINIC_AGGREGATE_RATING.worstRating,
  },
});

// -----------------------------------------------------------------------------
// BreadcrumbList — surfaces the "Home › Section › Page" trail directly in
// Google search results (measurable 3–8% CTR uplift on most physio sites).
//
// Usage:
//   getBreadcrumbLD([
//     { name: "Home", path: "/" },
//     { name: "Knowledge Base", path: "/knowledge-base" },
//   ])
//
// Every entry's `path` is joined onto PUBLIC_SITE_URL; the last item is treated
// as the current page automatically.
// -----------------------------------------------------------------------------
export const getBreadcrumbLD = (trail) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: (trail || []).map((step, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: step.name,
    item: `${PUBLIC_SITE_URL}${step.path.startsWith("/") ? step.path : `/${step.path}`}`,
  })),
});



// -----------------------------------------------------------------------------
// MedicalCondition — top-level medical entity for each service page.
// Google's medical knowledge panels reward this. Adding `signOrSymptom`,
// `possibleTreatment`, and `riskFactor` makes The TMD Physio eligible to
// appear on health-condition queries (e.g. "TMD symptoms").
//
// Usage:
//   getMedicalConditionLD({
//     name: "Temporomandibular Joint Disorder (TMD)",
//     alternateName: ["TMJ Disorder", "TMD"],
//     description: "…",
//     signs: ["Jaw pain", "Clicking or locking", "Headaches"],
//     treatments: ["Manual therapy", "Neuromodulation", "Postural retraining"],
//     riskFactors: ["Bruxism", "Chronic stress", "Dental malocclusion"],
//     slug: "tmd-treatment",
//   })
// -----------------------------------------------------------------------------
export const getMedicalConditionLD = ({
  name,
  alternateName = [],
  description,
  signs = [],
  treatments = [],
  riskFactors = [],
  slug,
}) => ({
  "@context": "https://schema.org",
  "@type": "MedicalCondition",
  "@id": `${PUBLIC_SITE_URL}/services/${slug}#condition`,
  name,
  ...(alternateName.length ? { alternateName } : {}),
  ...(description ? { description } : {}),
  url: `${PUBLIC_SITE_URL}/services/${slug}`,
  signOrSymptom: signs.map((s) => ({ "@type": "MedicalSignOrSymptom", name: s })),
  possibleTreatment: treatments.map((t) => ({
    "@type": "MedicalTherapy",
    name: t,
    provider: { "@id": CLINIC_ID },
  })),
  ...(riskFactors.length
    ? { riskFactor: riskFactors.map((r) => ({ "@type": "MedicalRiskFactor", name: r })) }
    : {}),
  associatedAnatomy: {
    "@type": "AnatomicalStructure",
    name: "Temporomandibular joint",
  },
});


// -----------------------------------------------------------------------------
// HowTo — schema for step-by-step KB articles (e.g. "5 jaw exercises for TMD").
// Google renders numbered steps directly in the SERP with images. Massive CTR
// boost on exercise/self-care queries.
//
// Usage:
//   getHowToLD({
//     name: "How to relieve jaw tension at home",
//     description: "Five gentle exercises you can do daily.",
//     image: "https://…",
//     totalTime: "PT10M",
//     steps: [
//       { name: "Warm compress", text: "Apply for 10 min…", image: "…" },
//       …
//     ],
//   })
// -----------------------------------------------------------------------------
export const getHowToLD = ({ name, description = "", image, totalTime, steps = [] }) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  name,
  ...(description ? { description } : {}),
  ...(image ? { image } : {}),
  ...(totalTime ? { totalTime } : {}),
  step: (steps || []).map((s, idx) => ({
    "@type": "HowToStep",
    position: idx + 1,
    name: s.name || `Step ${idx + 1}`,
    text: s.text || "",
    ...(s.image ? { image: s.image } : {}),
    ...(s.url ? { url: s.url } : {}),
  })),
});


// -----------------------------------------------------------------------------
// SpeakableSpecification — marks selected page elements as suitable for
// voice-assistant readback (Google Assistant, Siri, Alexa). When someone asks
// their assistant about TMD in London, the marked selectors are what get
// read aloud. Very few UK physios have this today — early-mover advantage.
//
// Usage on any page inside its <SEO structuredData={[...]}> array:
//   getSpeakableLD({
//     xpath: [
//       "/html/head/title",
//       "/html/head/meta[@name='description']/@content",
//     ],
//     cssSelector: [".hero-title", ".hero-description"],
//   })
// -----------------------------------------------------------------------------
export const getSpeakableLD = ({ cssSelector = [], xpath = [] } = {}) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  speakable: {
    "@type": "SpeakableSpecification",
    ...(cssSelector.length ? { cssSelector } : {}),
    ...(xpath.length ? { xpath } : {}),
  },
});


// -----------------------------------------------------------------------------
// ItemList — Google's structured data for ordered lists. When Google indexes
// an article that contains ItemList, it can surface the individual items in
// the SERP as a numbered snippet (typical for "5 tips…", "3 things…" style
// content) and expose them as a speakable "here's what you'll learn" for
// Google Assistant + Alexa. We use it for the LLM-generated `keyTakeaways`
// bullets on every KB article — reusing content we already have.
//
// Example:
//   getItemListLD({
//     name: "In this article, you'll learn",
//     items: ["Bullet 1", "Bullet 2", "Bullet 3"],
//     url: "https://example.com/knowledge-base/article-slug#takeaways",
//   })
// -----------------------------------------------------------------------------
export const getItemListLD = ({ name, items = [], url } = {}) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  ...(name ? { name } : {}),
  ...(url ? { url, "@id": `${url}` } : {}),
  numberOfItems: items.length,
  itemListOrder: "https://schema.org/ItemListOrderAscending",
  itemListElement: items.map((text, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    name: text,
  })),
});

